import JSZip from 'jszip'
import {APP_ICON_PLATFORMS, type AppIconPlatformId} from '../constants/appIconSizes'

export interface IconGradientStop {
  id: string
  color: string
  position: number
}

export interface IconComposeState {
  sourceImage: HTMLImageElement | null
  zoom: number
  offsetX: number
  offsetY: number
  bgMode: 'color' | 'gradient'
  bgColor: string
  bgGradient: IconGradientStop[]
  bgAngle: number
  textEnabled: boolean
  textValue: string
  textColor: string
  stripColor: string
}

const TEXT_STRIP_RATIO = 0.22
const TEXT_HORIZONTAL_PADDING_RATIO = 0.08

const paintBackground = (ctx: CanvasRenderingContext2D, size: number, state: IconComposeState): void => {
  if (state.bgMode === 'color') {
    ctx.fillStyle = state.bgColor
    ctx.fillRect(0, 0, size, size)
    return
  }
  const sorted = [...state.bgGradient].sort((a, b) => a.position - b.position)
  if (sorted.length === 0) {
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, size, size)
    return
  }
  const angleRad = ((state.bgAngle - 90) * Math.PI) / 180
  const cx = size / 2
  const cy = size / 2
  const halfDiag = (Math.SQRT2 * size) / 2
  const dx = Math.cos(angleRad) * halfDiag
  const dy = Math.sin(angleRad) * halfDiag
  const grad = ctx.createLinearGradient(cx - dx, cy - dy, cx + dx, cy + dy)
  sorted.forEach((stop) => grad.addColorStop(Math.max(0, Math.min(1, stop.position)), stop.color))
  ctx.fillStyle = grad
  ctx.fillRect(0, 0, size, size)
}

const paintImage = (ctx: CanvasRenderingContext2D, size: number, state: IconComposeState): void => {
  const img = state.sourceImage
  if (!img) return
  const imgW = img.naturalWidth || img.width
  const imgH = img.naturalHeight || img.height
  if (imgW === 0 || imgH === 0) return
  const baseScale = Math.min(size / imgW, size / imgH)
  const drawW = imgW * baseScale * state.zoom
  const drawH = imgH * baseScale * state.zoom
  const centerX = size / 2 + state.offsetX * size
  const centerY = size / 2 + state.offsetY * size
  ctx.imageSmoothingEnabled = true
  ctx.imageSmoothingQuality = 'high'
  ctx.drawImage(img, centerX - drawW / 2, centerY - drawH / 2, drawW, drawH)
}

const paintTextStrip = (ctx: CanvasRenderingContext2D, size: number, state: IconComposeState): void => {
  if (!state.textEnabled || state.textValue.trim() === '') return
  const stripHeight = size * TEXT_STRIP_RATIO
  const stripY = size - stripHeight
  ctx.fillStyle = state.stripColor
  ctx.fillRect(0, stripY, size, stripHeight)

  const horizontalPadding = size * TEXT_HORIZONTAL_PADDING_RATIO
  const maxWidth = size - horizontalPadding * 2
  let fontSize = stripHeight * 0.55
  const fontFamily = "'Roboto', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
  ctx.fillStyle = state.textColor
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  ctx.font = `600 ${fontSize}px ${fontFamily}`
  let measured = ctx.measureText(state.textValue).width
  if (measured > maxWidth) {
    fontSize = fontSize * (maxWidth / measured)
    ctx.font = `600 ${fontSize}px ${fontFamily}`
    measured = ctx.measureText(state.textValue).width
  }
  ctx.fillText(state.textValue, size / 2, stripY + stripHeight / 2)
}

export const composeIcon = (state: IconComposeState, size: number): HTMLCanvasElement => {
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const ctx = canvas.getContext('2d')
  if (!ctx) return canvas
  paintBackground(ctx, size, state)
  paintImage(ctx, size, state)
  paintTextStrip(ctx, size, state)
  return canvas
}

export const canvasToBlob = (canvas: HTMLCanvasElement): Promise<Blob> =>
  new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) resolve(blob)
      else reject(new Error('canvas.toBlob returned null'))
    }, 'image/png')
  })

export const generateIconZip = async (
  state: IconComposeState,
  platforms: ReadonlySet<AppIconPlatformId>,
): Promise<Blob> => {
  const zip = new JSZip()
  const selected = APP_ICON_PLATFORMS.filter((p) => platforms.has(p.id))
  for (const platform of selected) {
    for (const size of platform.sizes) {
      const canvas = composeIcon(state, size.pixels)
      const blob = await canvasToBlob(canvas)
      zip.file(`${platform.folder}/${size.fileName}`, blob)
    }
  }
  return zip.generateAsync({type: 'blob'})
}
